import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Globe, Mic, Rocket, Check, Loader2 } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { PageHero, IMG } from "../components/PageHero";
import { VoiceButton } from "../components/site/VoiceButton";
import { useAuth } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import { API } from "../lib/api";

const EXAMPLES = [
  "Mama Thandi Bakes – home bakery in Soweto selling vetkoek, cakes and scones. WhatsApp 082 555 1234",
  "Kofi's Auto Repairs in Accra – brakes, servicing and diagnostics, open Mon–Sat 8am–6pm",
  "Amani Safaris – small-group safari tours from Arusha to the Serengeti and Ngorongoro",
  "Lagos Glow Studio – braids, nails and bridal makeup in Lekki",
];
const STAGES = ["Understanding your business", "Writing your headline", "Describing your services", "Picking colours & photos", "Publishing preview"];

export default function WebsiteBuilder() {
  const { user } = useAuth();
  const { code } = useCountry();
  const nav = useNavigate();
  const [prompt, setPrompt] = useState(() => localStorage.getItem("ac_site_prompt") || "");
  const [busy, setBusy] = useState(false);
  const [chars, setChars] = useState(0);
  useEffect(() => { localStorage.setItem("ac_site_prompt", prompt); }, [prompt]);

  const generate = async () => {
    if (prompt.trim().length < 5) return toast.error("Tell us a little about your business first");
    if (!user) return nav("/register?next=/website-builder");
    setBusy(true); setChars(0);
    try {
      const res = await fetch(`${API}/sites/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ac_token")}` },
        body: JSON.stringify({ prompt, country: code }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Could not start the AI. Please try again.");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "progress") setChars(ev.chars);
          if (ev.type === "error") throw new Error(ev.message);
          if (ev.type === "done") {
            localStorage.removeItem("ac_site_prompt");
            toast.success("Your website is ready!");
            return nav(`/website-builder/${ev.site_id}`);
          }
        }
      }
      throw new Error("The AI stopped early. Please try again.");
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  const stage = Math.min(STAGES.length - 1, Math.floor(chars / 350));
  return (
    <div data-testid="website-builder-page">
      <PageHero eyebrow="100% free · AI website builder" title="Create a free website in 60 seconds." tone="pink" img={IMG.domain}
        sub="Just type or say what your business does. Our AI writes it, designs it and puts it online for free at hostafricair.com.">
        <div className="max-w-2xl rounded-3xl bg-white p-4 shadow-[0_30px_60px_-30px_rgba(27,16,51,0.45)]" data-testid="builder-prompt-card">
          {busy ? (
            <div className="space-y-3 p-2" data-testid="builder-progress">
              {STAGES.map((s, i) => (
                <p key={s} className={`flex items-center gap-2 text-sm font-semibold transition-opacity ${i <= stage ? "opacity-100" : "opacity-30"}`}>
                  {i < stage ? <Check className="h-4 w-4 text-secondary" /> : i === stage ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <span className="h-4 w-4" />}{s}
                </p>
              ))}
              <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${Math.min(95, 8 + chars / 20)}%` }} /></div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <Textarea rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="builder-prompt-input"
                  placeholder="e.g. A hair salon in Nairobi called Neema Beauty. We do braids, weaves and nails. Call 0712 345 678."
                  className="resize-none rounded-2xl border-0 bg-muted text-base focus-visible:ring-primary" />
                <VoiceButton onText={(t) => setPrompt((p) => (p ? `${p} ${t}` : t))} />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button onClick={generate} className="h-12 rounded-full px-7 text-base font-bold" data-testid="builder-generate-btn"><Wand2 className="mr-2 h-5 w-5" />Create my free website</Button>
                <span className="text-xs text-muted-foreground">No card needed. {user ? "" : "A free account saves your site."}</span>
              </div>
            </>
          )}
        </div>
        {!busy && (
          <div className="mt-5 flex max-w-2xl flex-wrap gap-2" data-testid="builder-examples">
            {EXAMPLES.map((e, i) => (
              <button key={i} onClick={() => setPrompt(e)} data-testid={`builder-example-${i}`} className="rounded-full bg-white/80 px-3 py-1.5 text-left text-xs font-medium text-foreground/80 transition-colors hover:bg-white hover:text-primary">{e.split(" – ")[0]}</button>
            ))}
          </div>
        )}
      </PageHero>
      <section className="mx-auto grid max-w-7xl gap-5 px-4 py-16 sm:px-6 md:grid-cols-4 lg:px-8">
        {[[Mic, "Type or speak", "Describe your business in any language."], [Wand2, "AI builds it", "Copy, colours, photos and layout in seconds."],
          [Rocket, "Live for free", "Published instantly at hostafricair.com/s/yourname."], [Globe, "Add your domain", "Connect .co.za, .africa or .com when you're ready."]].map(([I, t, d], i) => (
          <div key={t} className="fade-up rounded-3xl border bg-white p-6" style={{ animationDelay: `${i * 80}ms` }}>
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-accent text-primary"><I className="h-5 w-5" /></span>
            <p className="mt-4 text-xs font-bold text-primary">Step {i + 1}</p>
            <h3 className="text-lg font-bold">{t}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{d}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
